import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { DataCacheProvider } from "./contexts/DataCacheContext";
import { ClientViewProvider, useClientView } from "./contexts/ClientViewContext";
import { SidebarProvider, useSidebar } from "./contexts/SidebarContext";
import { DatabaseProvider } from "./contexts/DatabaseContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import Header from "./components/Header";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Insights from "./pages/Insights";
import CMS from "./pages/CMS";
import Clients from "./pages/Clients";
import ClientWorkspace from "./pages/ClientWorkspace";
import ClientPortal from "./pages/ClientPortal";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import SurveyPage from "./pages/SurveyPage";
import BookWorkshop from "./pages/BookWorkshop";
import DemoPortal from "./pages/DemoPortal";
import { useState, useEffect } from "react";

function AppContent() {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState("fadeIn");
  const { collapsed } = useSidebar();
  const { clientView, isClientMode } = useClientView();

  const isPublicPage = location.pathname === '/login' || location.pathname === '/signup' || location.pathname.startsWith('/survey/') || location.pathname === '/book' || location.pathname === '/demo' || location.pathname.startsWith('/demo/') ||
    displayLocation.pathname === '/login' || displayLocation.pathname === '/signup' || displayLocation.pathname.startsWith('/survey/') || displayLocation.pathname === '/book' || displayLocation.pathname === '/demo' || displayLocation.pathname.startsWith('/demo/');
  const isLoginRelated = isPublicPage;

  useEffect(() => {
    // No transitions for public pages (login, survey, book, demo)
    if (location.pathname === '/login' || location.pathname === '/signup' || location.pathname.startsWith('/survey/') || location.pathname === '/book' || location.pathname === '/demo' || location.pathname.startsWith('/demo/')) {
      setDisplayLocation(location);
      setTransitionStage('fadeIn');
      return;
    }

    // For all other navigation
    if (location.pathname !== displayLocation.pathname) {
      const isCMSNavigation = location.pathname.startsWith('/cms') && displayLocation.pathname.startsWith('/cms');
      const isClientTabNavigation = (
        (location.pathname.startsWith('/clients/') && displayLocation.pathname.startsWith('/clients/') &&
          location.pathname.split('/')[2] === displayLocation.pathname.split('/')[2]) ||
        (location.pathname.startsWith('/portal/') && displayLocation.pathname.startsWith('/portal/') &&
          location.pathname.split('/')[2] === displayLocation.pathname.split('/')[2])
      );

      // If coming from login or navigating within CMS/same client, no fade out needed
      if (displayLocation.pathname === '/login' || displayLocation.pathname === '/signup' || isCMSNavigation || isClientTabNavigation) {
        setDisplayLocation(location);
        setTransitionStage('fadeIn');
      } else {
        setTransitionStage('fadeOut');
      }
    }
  }, [location, displayLocation]);

  // Mobile: offset for fixed top header (pt-16) or header + banner (pt-24)
  // Desktop: offset for fixed left sidebar (pl-60 or pl-16), plus banner top offset if in clientView
  const contentClass = isLoginRelated
    ? ''
    : [
        (clientView && !isClientMode) ? 'pt-24 md:pt-8' : 'pt-16 md:pt-0',
        collapsed ? 'md:pl-20' : 'md:pl-64',
      ].join(' ');

  return (
    <div className="min-h-screen bg-background-light dark:bg-background text-text-primary-light dark:text-text-primary transition-colors duration-200">
      {!isLoginRelated && <Header />}
      <div
        className={`[transition:padding-left_300ms_cubic-bezier(0.4,0,0.2,1),opacity_200ms_ease] ${contentClass} ${
          transitionStage === "fadeOut" && !isLoginRelated ? "opacity-0" : "opacity-100"
        }`}
        onTransitionEnd={(e) => {
          if (e.propertyName === 'opacity' && transitionStage === "fadeOut") {
            setDisplayLocation(location);
            setTransitionStage("fadeIn");
          }
        }}
      >
        <Routes location={displayLocation}>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/survey/:surveyId" element={<SurveyPage />} />
          <Route path="/book" element={<BookWorkshop />} />
          <Route path="/demo" element={<DemoPortal />} />
          <Route path="/demo/:tab" element={<DemoPortal />} />

          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute requiredRoute="/dashboard"><Dashboard /></ProtectedRoute>} />
          <Route path="/insights" element={<ProtectedRoute requiredRoute="/insights"><Insights /></ProtectedRoute>} />
          <Route path="/cms" element={<ProtectedRoute requireCMS={true}><CMS /></ProtectedRoute>} />
          <Route path="/cms/:tab/:id" element={<ProtectedRoute requireCMS={true}><CMS /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute requiredRoute="/clients"><Clients /></ProtectedRoute>} />
          <Route path="/clients/:id" element={<ProtectedRoute requiredRoute="/clients"><ClientWorkspace /></ProtectedRoute>} />
          <Route path="/clients/:id/:tab" element={<ProtectedRoute requiredRoute="/clients"><ClientWorkspace /></ProtectedRoute>} />
          <Route path="/portal/:id" element={<ProtectedRoute requiredRoute="/clients"><ClientPortal /></ProtectedRoute>} />
          <Route path="/portal/:id/:tab" element={<ProtectedRoute requiredRoute="/clients"><ClientPortal /></ProtectedRoute>} />

          {/* Redirect all other routes to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <DataCacheProvider>
          <AuthProvider>
            <DatabaseProvider>
              <ClientViewProvider>
                <SidebarProvider>
                  <AppContent />
                </SidebarProvider>
              </ClientViewProvider>
            </DatabaseProvider>
          </AuthProvider>
        </DataCacheProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
